// Base de toda tela v2 (_LayoutV2): topo, menu, barra inferior, cookies, sessão, PWA e terceiros.
// Chama cada init explicitamente: o package.json declara sideEffects:false, e import só por efeito seria descartado.
import { aoExibirPagina, config } from './v2/config';
import { aoExpirarSessao } from './v2/api';
import { iniciarAviso, mostrarSessaoExpirada } from './v2/aviso';
import { iniciarConsentimento } from './v2/consentimento';
import { iniciarSobreposicao } from './v2/sobreposicao';
import { atualizarContadorSalvas, sincronizarSalvas } from './v2/salvas';
import { esquecerLogado, iniciarTopo } from './v2/topo';
import { iniciarTerceiros } from './v2/terceiros';
import { aoMudarLogin, obterUsuario } from './v2/usuario';
import { iniciarMedicaoPagina } from './v2/medicao';

function _registrarPwa() {
    // Mesmo service worker do v1, só no domínio principal (o servidor decide e manda em config.pwa).
    if (!config().pwa || !('serviceWorker' in navigator)) {
        return;
    }
    window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' }).catch(() => { });
    }, { once: true });
}

// Pré-render das públicas (vêm da borda) e só pré-busca das telas da conta que só leem (CANDIDATE.md §8.3). :c e :s ficam num
// segmento: o * passaria por / (/api/..., /vagas/denunciar).
function _precarregar() {
    // No hotsite as tags e os scripts da empresa rodariam na página pré-renderizada e contariam a visita.
    if (config().hotsite || !HTMLScriptElement.supports?.('speculationrules')) {
        return;
    }
    // No navegador, só no toque ou no clique: no celular o moderate do Chrome pré-carrega sozinho quando a rolagem para.
    const app = document.documentElement.hasAttribute('data-app');
    const moderado = app ? 'moderate' : 'conservative';
    // Fora: Sair, download, nova aba, o card de vaga e as parecidas (abrem na gaveta), o que o JS trata sem trocar de tela (abas,
    // busca em tela cheia, a página atual) e os retornos que agem ao carregar (confirmar=1 envia a candidatura; vaga= e alerta=).
    const fora = { not: { selector_matches: '[data-sair], [download], [target], [data-vaga], [data-sim], [data-tab], [data-sov], [aria-current="page"], [href*="confirmar="], [href*="vaga="], [href*="alerta="]' } };
    const regra = (enderecos, eagerness, alem = []) => ({
        where: { and: [{ or: enderecos.map(e => ({ href_matches: e })) }, fora, ...alem] },
        eagerness
    });
    const regras = document.createElement('script');
    regras.type = 'speculationrules';
    regras.textContent = JSON.stringify({
        // As salvas leem a lista do aparelho ao carregar: pré-renderizadas, mostrariam a lista de antes.
        prerender: [regra(['/pt-br', '/en-us', '/es-cl', '/:c/vagas', '/:c/vagas-:s', '/:c/vaga-:s', '/:c/trabalhe-na-:s', '/:c/profissoes', '/:c/profissoes/:s'],
            moderado, [{ not: { href_matches: '/:c/vagas-salvas' } }])],
        prefetch: [
            regra(['/:c/candidato/candidaturas', '/:c/candidato/inscricao_:id', '/:c/candidato/alerta', '/:c/candidato/mensagens',
                '/:c/candidato/clube-beneficios', '/:c/candidato/configuracoes'], moderado),
            // O GET do Início e o do currículo gravam (contador do topo, progresso do currículo): só no app e no toque. No navegador
            // fica fora, porque o toque que começa uma rolagem em cima do link também dispara a pré-busca.
            ...(app ? [regra(['/:c/candidato/painel', '/:c/candidato/curriculo'], 'conservative')] : [])
        ]
    });
    document.head.append(regras);
}

// As telas que não medem por conta própria (as da conta, por exemplo) entram no GA4 com o nome da aba.
function _medirSeFaltar() {
    window.addEventListener('load', () => {
        if (!window.medicaoV2Iniciada) {
            iniciarMedicaoPagina(config().aba || 'outra');
        }
    }, { once: true });
}

iniciarSobreposicao();
iniciarAviso();
// Pré-renderizada, a página lê cookies e salvas quando é aberta: a pessoa pode ter aceitado ou salvado algo na tela anterior.
aoExibirPagina(iniciarConsentimento);
iniciarTopo();
iniciarTerceiros();
aoExibirPagina(atualizarContadorSalvas);
obterUsuario().then(u => aoExibirPagina(() => sincronizarSalvas(u)));
_registrarPwa();
_precarregar();
_medirSeFaltar();
// iPhone: o Safari só aplica o :active (o "apertado" dos controles, _complementos.scss) com um ouvinte de toque no corpo.
document.body.addEventListener('touchstart', () => {}, { passive: true });
// "Dar opinião": o código só baixa nas telas da lista do _OpiniaoV2.
if (document.getElementById('opn-abrir')) {
    import(/* webpackChunkName: "opiniao" */ './v2/opiniao').then(({ iniciarOpiniao }) => iniciarOpiniao()).catch(() => { });
}
// "Nossas informações" do rodapé do hotsite fora da home: busca a Employer Page quando o rodapé se aproxima.
if (document.querySelector('[data-hs-info]')) {
    import(/* webpackChunkName: "hotsite-rodape" */ './v2/hotsite-rodape').then(({ iniciarRodapeHotsite }) => iniciarRodapeHotsite()).catch(() => { });
}

aoExpirarSessao(() => {
    esquecerLogado();
    mostrarSessaoExpirada();
});
// A janela de retorno do Google/LinkedIn fica fora: o "login" que a página de origem avisa chegava antes de ela fechar, e o reload
// pedia de novo o endereço com o código do provedor, já usado.
if (!config().retornoLogin) {
    aoMudarLogin(async evento => {
        if (evento === 'login') {
            window.location.reload();
            return;
        }
        if (evento === 'logout' && (await obterUsuario()).logado) {
            mostrarSessaoExpirada();
        }
    });
}
